import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { getLocalDayRange } from '@/shared/lib/time-utils';

import type { GetScheduledSessionsInput, SessionListFilters } from '../model/session.types';
import { sessionsApi } from './session.api';

export function toDayKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export const queryKeys = {
    all: ['sessions'] as const,
    days: () => [...queryKeys.all, 'day'] as const,
    day: (date: Date = new Date()) => [...queryKeys.days(), toDayKey(date)] as const,
    lists: () => [...queryKeys.all, 'list'] as const,
    list: (params: SessionListFilters) => [...queryKeys.lists(), params] as const,
    detail: (id: string) => [...queryKeys.all, 'detail', id] as const,
    last: (clientId?: string) => [...queryKeys.all, 'last', clientId] as const,
    clientActivity: (clientId: string) => [...queryKeys.all, 'client-activity', clientId] as const,
    clientServiceCodes: (clientId: string) =>
        [...queryKeys.all, 'client-service-codes', clientId] as const,
    scheduled: (startDate: string, endDate: string) =>
        [...queryKeys.all, 'scheduled', startDate, endDate] as const,
};

export function sessionsByDayOptions(date: Date = new Date()) {
    return queryOptions({
        queryKey: queryKeys.day(date),
        queryFn: async ({ signal }) => {
            const response = await sessionsApi.list(
                {
                    ...getLocalDayRange(date),
                    order: 'desc',
                },
                signal,
            );
            return response.items;
        },
        staleTime: 60 * 1000,
        refetchOnWindowFocus: 'always',
    });
}

export function sessionListOptions(params: SessionListFilters) {
    return infiniteQueryOptions({
        queryKey: queryKeys.list(params),
        queryFn: async ({ pageParam, signal }) => {
            return await sessionsApi.list(
                {
                    ...params,
                    cursor: pageParam,
                    page_size: 50,
                    end_date: new Date().toISOString(),
                },
                signal,
            );
        },
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
    });
}

export function sessionByIdOptions(id: string = '') {
    return queryOptions({
        queryKey: queryKeys.detail(id),
        queryFn: ({ signal }) => sessionsApi.getById(id, signal),
        staleTime: Infinity,
        enabled: !!id,
    });
}

export function lastSessionOptions(clientId?: string) {
    return queryOptions({
        queryKey: queryKeys.last(clientId),
        queryFn: async ({ signal }) => {
            const response = await sessionsApi.list(
                {
                    client_id: clientId,
                    order: 'desc',
                    page_size: 1,
                },
                signal,
            );

            return response.items[0] || null;
        },
        staleTime: Infinity,
    });
}

export function clientActivityOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.clientActivity(clientId),
        queryFn: ({ signal }) => sessionsApi.getClientActivity(clientId, signal),
        enabled: !!clientId,
    });
}

export function clientServiceCodesOptions(clientId: string) {
    return queryOptions({
        queryKey: queryKeys.clientServiceCodes(clientId),
        queryFn: ({ signal }) => sessionsApi.getClientServiceCodes(clientId, signal),
        enabled: !!clientId,
    });
}

export function scheduledSessionsOptions({ startDate, endDate }: GetScheduledSessionsInput) {
    return queryOptions({
        queryKey: queryKeys.scheduled(startDate, endDate),
        queryFn: ({ signal }) => sessionsApi.getScheduled({ startDate, endDate }, signal),
    });
}
